import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import ErrorMessage from "@/components/common/ErrorMessage";
import { verifyEmailOtpSchema } from "@backend/src/schemas/authSchema";
import { Button } from "@/components/ui/button";
import { isAxiosError } from "axios";
import { z } from "zod";
import { useUser } from "@/hooks/useUser";
import useVerifyEmailMutation from "@/api/mutation/useVerifyEmailMutation";
import InputText from "@/components/common/InputText";
import FormLabel from "@/components/common/FormLabel";
import useTimer from "@/hooks/useTimer";
import useResendVerifyEmailOtpMutation from "@/api/mutation/useResendVerifyEmailOtpMutation";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import UserVerifiedIcon from "../../../assets/svg/UserVerified.svg";
import { toast } from "react-toastify";
import BackgroundImage from "@/components/layout/BackgroundImage";
import NewLogoTitle from "@/assets/svg/NewLogoTitle.svg";
import { errorConfig } from "@/lib/toastError";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";

export type LoginValues = {
  email: string;
  password: string;
};

function Verification() {
  const localLastSent = localStorage.getItem("lastSent");
  const { user } = useUser();
  const [lastSentTime, setLastSentTime] = useState(
    localLastSent ? parseInt(localLastSent) : null
  );
  const { time, resetTimer } = useTimer(
    lastSentTime
      ? Math.max(60 - Math.floor((Date.now() - lastSentTime) / 1000), 0)
      : 0
  );
  const [count, setCount] = useState(0);
  const { t } = useTranslation();
  const verifyEmailMutation = useVerifyEmailMutation();
  const resendVerifyEmailOtpMutation = useResendVerifyEmailOtpMutation();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState("");
  const navigate = useNavigate();
  const { refetch, status , isError } = useCurrentUserQuery();
  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (status === "success") {
      if (!user?.email && isError) {
        navigate("/login");
      }
      if (user && user.isVerified) {
        setOtpVerified(true);
      }
    }
  }, [user]);
  useEffect(() => {
    if (isVerifying) {
      const timer = setInterval(() => {
        setCount((prevCount) => prevCount + 1);
      }, 1000);

      if (count >= 30) {
        clearInterval(timer);
        navigate("/organisation", { state: { showVideo: true } });
      }
      return () => clearInterval(timer);
    }
  }, [count, isVerifying]);
  const [otpVerified, setOtpVerified] = useState(false);
  const formik = useFormik<z.infer<typeof verifyEmailOtpSchema>>({
    initialValues: {
      otp: "",
    },
    validationSchema: toFormikValidationSchema(verifyEmailOtpSchema),
    onSubmit: (values, helper) => {
      setIsVerifying(true);
      verifyEmailMutation.mutate(values, {
        onSuccess(data) {
          toast.success(data.data.message);
          refetch();
          setOtpVerified(true);
        },
        onError(error) {
          if (isAxiosError(error)) {
            setIsVerifying(false);
            if (
              error.response?.status === 400 &&
              error.response.data?.errors &&
              Array.isArray(error.response?.data.errors)
            ) {
              error.response.data.errors.forEach((item) => {
                helper.setFieldError(item.path[0], item.message);
              });
            }
            if (!Array.isArray(error.response?.data.errors)) {
              toast.error(
                errorKeyFormate(error.response?.data.message + ""),
                errorConfig
              );
            }
          }
        },
      });
    },
  });

  return (
    <>
      <div className="flex flex-col px-2 items-center min-h-screen py-6 justify-center  bg-secondary-950">
        <BackgroundImage />
        <div className="absolute bg-primary-500/20 rounded-br-3xl px-40 rounded-bl-3xl mb-5 h-24 w-auto top-0  left-1/2 -translate-x-1/2 flex justify-center items-center">
          <img src={NewLogoTitle} className="h-10" />
        </div>
        <div className="w-[min(450px,100%)] space-y-4 py-4 overflow-hidden bg-white shadow-md sm:max-w-lg sm:rounded-lg z-10">
          <div className="px-4">
            <h3 className="text-2xl text-center font-bold text-primary-900">
              {!otpVerified ? t("Verify Email") : t("Email Verified ")}
            </h3>
          </div>
          <hr />
          {!otpVerified ? (
            <form onSubmit={formik.handleSubmit} className="px-4">
              <div className="mt-4 text-gray-600">
                {t("We have sent an OTP (One time password) to your email")}"
                <span className="font-bold">{user?.email}</span>"
              </div>
              <div className="relative w-full mt-4">
                <FormLabel htmlFor="otp">{t("One time password")}</FormLabel>
                <InputText
                  name="otp"
                  placeholder="Please enter otp"
                  value={formik.values.otp}
                  onChange={formik.handleChange}
                />
                <div>
                  <ErrorMessage>
                    {formik.touched.otp && formik.errors.otp}
                  </ErrorMessage>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-danger text-sm">{resendError}</span>
                <Button
                  variant={"primary_outline"}
                  className="h-auto py-1 px-2"
                  onClick={() => {
                    setIsResending(true);
                    resendVerifyEmailOtpMutation.mutate(null, {
                      onSuccess: (data) => {
                        const t = Date.now();
                        localStorage.setItem("lastSent", `${t}`);
                        resetTimer(60);
                        setLastSentTime(t);
                        setIsResending(false);
                        setResendError("");
                        toast.success(data.data.message);
                      },
                      onError: (error) => {
                        setResendError("Failed to resend OTP");
                        setIsResending(false);
                        toast.error(
                          errorKeyFormate(error.response?.data.message + ""),
                          errorConfig
                        );
                      },
                    });
                  }}
                  isLoading={isResending}
                  disabled={time !== 0 || isResending}
                >
                  {isResending
                    ? t("Resend OTP")
                    : time === 0
                      ? t("Resend OTP")
                      : `${t("Resend OTP")} in ${time}`}
                </Button>
              </div>
              <div className="flex items-center mt-1">
                <Button
                  type="submit"
                  variant={"primary"}
                  className="w-full"
                  isLoading={isVerifying}
                  disabled={isVerifying}
                >
                  {t("Verify")}
                </Button>
              </div>
              <div className="text-[0.7rem] mt-5">
                {t("Check your inbox and spam folder. If you don’t receive the OTP within 15 min, your email provider might have blocked it. Try using a different email")}
              </div>
            </form>
          ) : (
            <div
              className="rounded-lg p-3 bg-white  flex flex-col items-center justify-around
           "
            >
              <div>
                <img src={UserVerifiedIcon} alt="" width={50} />
              </div>
              <div className="mt-4 text-lg text-gray-600 text-center">
                {t("Congratulations!")}
                <br />
                {t("Your email has been successfully verified.")}
              </div>
              <div className="mt-3 w-full">
                <Button
                  variant={"primary"}
                  onClick={() =>
                    navigate("/organisation", { state: { showVideo: true } })
                  }
                  className="w-full"
                >
                  {t("Access my space")}
                </Button>
                <div className="text-center text-xs mt-5">
                  {t("Automatically redirect in")}{" "}
                  {(count - 30).toString().substring(1)} {t("seconds")}.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Verification;
